import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Btn, DataTable, KPI, KPIRow, Loading, Msg, Section, fmtDate, panelGet, panelPost } from '../kit';
import { inputCls } from '../email/emailKit';
import { AdminLink } from '../adminNav';
import {
  type ConfigData, type Note, Pill, S, ago, asArray, asStr, first, fmt, num, rowsOf,
} from './shared';

/**
 * Admin → Onboarding → Cosmos picks: the precomputed top-3 lists an Explorer
 * lands on (spec 13 Oct 2026, "precompute the top 3 results").
 *
 *   84 lists  industry × keyword chip
 *   14 lists  industry only
 *    1 pool   20–30 ids; a skipped user gets 3 of them, stable per user
 *
 * Each list keeps 10 ranked ids (3 shown + 7 back-ups), so a technology that
 * leaves the corpus is replaced by the next one without a rebuild. Built in
 * the background by Backend/onboarding_picks.py — after a corpus load (it is a
 * step of System → Search indexes), when the chips change, and on Refresh.
 *
 * Pins come first, in order; excluded ids never show. Both survive rebuilds.
 */

const POOL = '*';

interface PickItem {
  id: string; title: string; university: string; trl: number | null; score: number | null;
  source: string; in_corpus: boolean | null; pinned: boolean; excluded: boolean;
  /** false = in the corpus but too thin to show (no title / short summary). */
  healthy: boolean | null;
}
interface PickList {
  industry: string; keyword: string; kind: string; items: PickItem[];
  pinned_ids: string[]; excluded_ids: string[]; candidates: number | null; orphaned: boolean;
  built_at: string | null;
}

const normItem = (r: any, pinned: string[], excluded: string[]): PickItem | null => {
  if (r === null || r === undefined) return null;
  if (typeof r === 'string') {
    return { id: r, title: '', university: '', trl: null, score: null, source: '', in_corpus: null,
      pinned: pinned.includes(r), excluded: excluded.includes(r), healthy: null };
  }
  const id = asStr(first(r.id, r.tech_id, r.utech_technology_id));
  if (!id) return null;
  const inCorpus = first(r.in_corpus, r.exists, r.present);
  const healthy = r.healthy;
  return {
    id,
    title: asStr(first(r.title, r.utech_title, r.name)),
    university: asStr(first(r.university, r.university_name, r.uni)),
    trl: num(first(r.trl, r.calculated_trl)),
    score: num(r.score),
    source: asStr(first(r.source, r.method)),
    in_corpus: typeof inCorpus === 'boolean' ? inCorpus : null,
    pinned: r.pinned === true || pinned.includes(id),
    excluded: r.excluded === true || excluded.includes(id),
    healthy: typeof healthy === 'boolean' ? healthy : null,
  };
};

const normList = (r: any): PickList | null => {
  if (!r || typeof r !== 'object') return null;
  const pinned = asArray(first(r.pinned_ids, r.pinned)).map(asStr);
  const excluded = asArray(first(r.excluded_ids, r.excluded)).map(asStr);
  // onboarding_picks.lists_view: `entries` (ranked, exclusions removed) and
  // `excluded` ([{id, title, in_corpus}]); `shown` there is a count.
  let itemsRaw = asArray(first(r.entries, r.items, r.cards, r.rows));
  if (!itemsRaw.length && (Array.isArray(r.shown) || r.backups)) itemsRaw = [...asArray(r.shown), ...asArray(r.backups)];
  if (!itemsRaw.length) itemsRaw = asArray(first(r.ids, r.auto_ids));
  const items = itemsRaw.map(x => normItem(x, pinned, excluded)).filter(Boolean) as PickItem[];
  asArray(r.excluded).forEach((x: any) => {
    if (x && typeof x === 'object' && !items.some(i => i.id === asStr(x.id))) {
      const it = normItem(x, pinned, excluded);
      if (it) items.push({ ...it, excluded: true });
    }
  });
  // Pins and exclusions that the hydrated list does not carry still show.
  pinned.forEach(id => { if (!items.some(i => i.id === id)) items.unshift(normItem(id, pinned, excluded)!); });
  excluded.forEach(id => { if (!items.some(i => i.id === id)) items.push(normItem(id, pinned, excluded)!); });
  const industry = asStr(first(r.industry, r.list_key?.split?.('|')?.[0]));
  return {
    industry,
    keyword: asStr(first(r.keyword, r.list_key?.split?.('|')?.[1], '')),
    kind: asStr(first(r.kind, industry === POOL ? 'pool' : (r.keyword ? 'keyword' : 'industry'))),
    items,
    pinned_ids: pinned,
    excluded_ids: excluded,
    candidates: num(first(r.candidates, r.candidate_count)),
    orphaned: r.orphaned === true,
    built_at: first(r.built_at, r.updated_at) ?? null,
  };
};

const listsOf = (d: any): PickList[] =>
  rowsOf(d, 'lists', 'picks').map(normList).filter(Boolean) as PickList[];

/** Served order: pins first (their order), then the rest, exclusions removed. */
const servedOrder = (l: PickList): PickItem[] => {
  const pinned = l.pinned_ids.map(id => l.items.find(i => i.id === id)).filter(Boolean) as PickItem[];
  const rest = l.items.filter(i => !l.pinned_ids.includes(i.id) && !l.excluded_ids.includes(i.id));
  return [...pinned, ...rest].filter(i => i.in_corpus !== false && i.healthy !== false);
};

const SOURCE_TEXT: Record<string, string> = {
  strict: 'sub-sector match', text: 'text match', industry_fill: 'from the industry list', pool: 'pool',
  pinned: 'pinned', pin: 'pinned', industry: 'industry', popular: 'popular',
};

// ---------------------------------------------------------------------------
// One list: the 3 shown, the 7 back-ups, pin / exclude, live preview.
// ---------------------------------------------------------------------------
const ListCard: React.FC<{ list: PickList; onChanged: () => void }> = ({ list, onChanged }) => {
  const [pinned, setPinned] = useState<string[]>(list.pinned_ids);
  const [excluded, setExcluded] = useState<string[]>(list.excluded_ids);
  const [addId, setAddId] = useState('');
  const [busy, setBusy] = useState<'' | 'save' | 'preview'>('');
  const [msg, setMsg] = useState<Note>(null);
  const [preview, setPreview] = useState<PickItem[] | null>(null);
  useEffect(() => { setPinned(list.pinned_ids); setExcluded(list.excluded_ids); }, [list]);
  const dirty = JSON.stringify(pinned) !== JSON.stringify(list.pinned_ids)
    || JSON.stringify(excluded) !== JSON.stringify(list.excluded_ids);

  const draftList: PickList = { ...list, pinned_ids: pinned, excluded_ids: excluded };
  const known = new Set(list.items.map(i => i.id));
  const extraPins = pinned.filter(id => !known.has(id))
    .map(id => ({ id, title: '(checked on Save)', university: '', trl: null, score: null, source: 'pinned',
      in_corpus: null, pinned: true, excluded: false, healthy: null } as PickItem));
  const served = servedOrder({ ...draftList, items: [...extraPins, ...list.items] });
  const excludedItems = list.items.filter(i => excluded.includes(i.id));
  const thinItems = list.items.filter(i => i.healthy === false && !excluded.includes(i.id));
  const goneItems = list.items.filter(i => i.in_corpus === false && !excluded.includes(i.id));

  const pinAt = (id: string, pos: number) => {
    const next = pinned.filter(x => x !== id);
    next.splice(Math.max(0, Math.min(pos - 1, next.length)), 0, id);
    setPinned(next);
    setExcluded(excluded.filter(x => x !== id));
  };
  const unpin = (id: string) => setPinned(pinned.filter(x => x !== id));
  const exclude = (id: string) => { setExcluded([...excluded.filter(x => x !== id), id]); unpin(id); };
  const restore = (id: string) => setExcluded(excluded.filter(x => x !== id));

  const save = async () => {
    setBusy('save'); setMsg(null);
    const { error } = await panelPost(S, list.kind === 'pool' ? 'pool_set' : 'picks_pin',
      list.kind === 'pool'
        ? { pinned, excluded }
        : { industry: list.industry, keyword: list.keyword, pinned_ids: pinned, excluded_ids: excluded });
    setBusy('');
    if (error) { setMsg({ kind: 'error', text: error }); return; }
    setMsg({ kind: 'ok', text: 'Saved — Explorers get it on their next landing.' });
    onChanged();
  };
  const runPreview = async () => {
    setBusy('preview'); setMsg(null);
    const { data, error } = await panelGet(S, 'picks_preview', { industry: list.industry, keyword: list.keyword });
    setBusy('');
    if (error) { setMsg({ kind: 'error', text: `Preview failed: ${error}` }); return; }
    const l = normList(data?.list || data) || listsOf(data)[0];
    setPreview(l ? l.items : rowsOf(data, 'items').map((x: any) => normItem(x, [], [])).filter(Boolean) as PickItem[]);
  };

  const title = list.kind === 'pool' ? 'Random pool (skipped onboarding, “Others”)'
    : list.keyword ? `${list.industry} · ${list.keyword}` : `${list.industry} — industry only`;

  const row = (it: PickItem, i: number, shown: boolean) => (
    <div key={it.id} className={`flex items-start gap-2 rounded-lg border p-2 ${shown ? 'border-primary-300 bg-primary-100/30' : 'border-grayscale-300 bg-white'}`}>
      <span className="text-xs text-grayscale-1100 w-5 text-right shrink-0 pt-0.5">{i + 1}</span>
      <div className="flex-1 min-w-0">
        <div className="text-sm text-grayscale-1300 truncate" title={it.title || it.id}>{it.title || it.id}</div>
        <div className="text-[11px] text-grayscale-1100 flex flex-wrap gap-x-2">
          {it.university && <span>{it.university}</span>}
          {it.trl !== null && <span>TRL {it.trl}</span>}
          {it.score !== null && <span>score {Math.round(it.score * 100) / 100}</span>}
          {it.source && <span>{SOURCE_TEXT[it.source] || it.source}</span>}
          <span className="font-mono">{it.id}</span>
        </div>
      </div>
      <span className="flex items-center gap-1 shrink-0 flex-wrap justify-end">
        {pinned.includes(it.id) && <Pill tone="tan">Pinned</Pill>}
        {it.in_corpus === false && <Pill tone="bad">Gone from corpus</Pill>}
        {pinned.includes(it.id)
          ? <Btn small onClick={() => unpin(it.id)}>Unpin</Btn>
          : (
            <select aria-label="Pin at position" value=""
              onChange={e => { if (e.target.value) pinAt(it.id, Number(e.target.value)); }}
              className="h-7 px-1 rounded-md border-[0.5px] border-primary-300 bg-white text-xs">
              <option value="">Pin…</option>
              {[1, 2, 3].map(p => <option key={p} value={p}>at {p}</option>)}
            </select>
          )}
        <Btn small kind="danger" onClick={() => exclude(it.id)} title="Never show this technology in this list">Exclude</Btn>
      </span>
    </div>
  );

  return (
    <div className="rounded-xl border border-grayscale-300 bg-white p-3 flex flex-col gap-2">
      <div className="flex items-center gap-2 flex-wrap">
        <span className="text-sm font-bold text-grayscale-1300">{title}</span>
        {list.candidates !== null && (
          <Pill tone={list.candidates < 3 ? 'warn' : 'grey'}>{fmt(list.candidates)} candidate{list.candidates === 1 ? '' : 's'}</Pill>
        )}
        {list.orphaned && <Pill tone="warn" title="Its chip was removed; kept because it has pins">Orphaned</Pill>}
        {list.built_at && <span className="text-[11px] text-grayscale-1100">built {ago(list.built_at)}</span>}
        <span className="flex-1" />
        {list.kind !== 'pool' && (
          <Btn small onClick={runPreview} disabled={!!busy} title="Compute this list live from the corpus now (not saved)">
            {busy === 'preview' ? 'Computing…' : 'Preview live'}
          </Btn>
        )}
      </div>

      <div className="flex flex-col gap-1.5">
        {served.slice(0, 3).map((it, i) => row(it, i, true))}
        {served.length === 0 && <div className="text-xs text-amber-700">Empty — Explorers on this choice get the industry list, then the pool.</div>}
        {served.length > 3 && (
          <details>
            <summary className="text-xs text-grayscale-1100 cursor-pointer select-none py-1">
              {served.length - 3} back-up{served.length - 3 === 1 ? '' : 's'} (shown when one above leaves the corpus)
            </summary>
            <div className="flex flex-col gap-1.5 mt-1">{served.slice(3).map((it, i) => row(it, i + 3, false))}</div>
          </details>
        )}
        {(thinItems.length > 0 || goneItems.length > 0) && (
          <div className="text-[11px] text-grayscale-1100">
            Skipped when served: {[
              goneItems.length ? `${goneItems.length} gone from the corpus` : '',
              thinItems.filter(i => i.in_corpus !== false).length
                ? `${thinItems.filter(i => i.in_corpus !== false).length} without a title or a full summary` : '',
            ].filter(Boolean).join(', ')}
          </div>
        )}
        {excludedItems.length > 0 && (
          <div className="text-xs text-grayscale-1100 flex flex-wrap items-center gap-1.5">
            <span>Excluded:</span>
            {excludedItems.map(it => (
              <span key={it.id} className="inline-flex items-center gap-1 rounded-full border border-grayscale-300 px-2 py-0.5">
                <span className="max-w-[16rem] truncate" title={it.title || it.id}>{it.title || it.id}</span>
                <button type="button" className="underline" onClick={() => restore(it.id)}>restore</button>
              </span>
            ))}
            {excluded.filter(id => !known.has(id)).map(id => (
              <span key={id} className="inline-flex items-center gap-1 rounded-full border border-grayscale-300 px-2 py-0.5 font-mono">
                {id} <button type="button" className="underline font-sans" onClick={() => restore(id)}>restore</button>
              </span>
            ))}
          </div>
        )}
      </div>

      <div className="flex items-center gap-2 flex-wrap">
        <div className="w-56">
          <input value={addId} placeholder="Pin a tech id (UTECH-…)" onChange={e => setAddId(e.target.value.trim())}
            onKeyDown={e => { if (e.key === 'Enter' && addId) { pinAt(addId, pinned.length + 1); setAddId(''); } }}
            className={inputCls} />
        </div>
        <Btn small onClick={() => { if (addId) { pinAt(addId, pinned.length + 1); setAddId(''); } }} disabled={!addId}>Pin</Btn>
        <span className="flex-1" />
        <Btn kind="primary" small onClick={save} disabled={!dirty || !!busy}>{busy === 'save' ? 'Saving…' : 'Save pins'}</Btn>
        {dirty && !busy && <Btn small onClick={() => { setPinned(list.pinned_ids); setExcluded(list.excluded_ids); }}>Discard</Btn>}
      </div>
      {msg && <Msg kind={msg.kind}>{msg.text}</Msg>}
      {preview && (
        <div className="rounded-lg border border-dashed border-grayscale-400 p-2 flex flex-col gap-1">
          <div className="text-[11px] uppercase tracking-wide text-grayscale-1100 flex items-center justify-between">
            <span>Live preview (not saved; pins not applied)</span>
            <button type="button" className="underline normal-case tracking-normal" onClick={() => setPreview(null)}>close</button>
          </div>
          {preview.length ? preview.map((it, i) => (
            <div key={it.id} className="text-xs text-grayscale-1200 flex gap-2">
              <span className="w-5 text-right text-grayscale-1100">{i + 1}</span>
              <span className="flex-1 truncate">{it.title || it.id}</span>
              <span className="text-grayscale-1100">{it.university}{it.trl !== null ? ` · TRL ${it.trl}` : ''}</span>
            </div>
          )) : <div className="text-xs text-grayscale-1100">No candidates.</div>}
        </div>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// The tab
// ---------------------------------------------------------------------------
const Picks: React.FC<{ data: ConfigData }> = ({ data }) => {
  const [status, setStatus] = useState<any>(null);
  const [statusErr, setStatusErr] = useState('');
  const [builds, setBuilds] = useState<any[]>([]);
  const [industry, setIndustry] = useState('');
  const [lists, setLists] = useState<PickList[] | null>(null);
  const [listsErr, setListsErr] = useState('');
  const [pool, setPool] = useState<PickList | null>(null);
  const [poolSize, setPoolSize] = useState('');
  const [busy, setBusy] = useState<'' | 'rebuild' | 'pool'>('');
  const [msg, setMsg] = useState<Note>(null);
  const poll = useRef<ReturnType<typeof setTimeout> | null>(null);

  const names = useMemo(() => {
    const n = data.all_names.length ? data.all_names : data.industries.map(i => i.name);
    return n.filter(x => x !== 'Others');
  }, [data]);
  useEffect(() => { if (!industry && names.length) setIndustry(names[0]); }, [names, industry]);

  const loadStatus = useCallback(async () => {
    const { data: d, error } = await panelGet(S, 'picks_status');
    if (error) { setStatusErr(error); return null; }
    setStatusErr(''); setStatus(d);
    return d;
  }, []);
  const loadBuilds = useCallback(async () => {
    const { data: d } = await panelGet(S, 'picks_builds');
    setBuilds(rowsOf(d, 'builds'));
  }, []);
  const loadLists = useCallback(async (ind: string) => {
    if (!ind) return;
    setLists(null); setListsErr('');
    const { data: d, error } = await panelGet(S, 'picks', { industry: ind });
    if (error) { setListsErr(error); setLists([]); return; }
    const all = listsOf(d).filter(l => l.kind !== 'pool' && (l.industry === ind || !l.industry));
    // Industry-only first, then the chips in their display order.
    const chipOrder = data.industries.find(i => i.name === ind)?.chips || [];
    all.sort((a, b) => (a.keyword ? 1 : 0) - (b.keyword ? 1 : 0)
      || chipOrder.indexOf(a.keyword) - chipOrder.indexOf(b.keyword));
    setLists(all);
  }, [data]);
  const loadPool = useCallback(async () => {
    const { data: d } = await panelGet(S, 'picks', { industry: POOL });
    const p = listsOf(d).find(l => l.kind === 'pool' || l.industry === POOL) || null;
    setPool(p);
    const size = num(first(d?.pool_size, status?.knobs?.pool_size, status?.pool_size, data.config.picks?.pool_size));
    setPoolSize(size !== null ? String(size) : String(p?.items.length || 24));
  }, [data, status]);

  useEffect(() => { void loadStatus(); void loadBuilds(); void loadPool(); // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => { void loadLists(industry); }, [industry, loadLists]);
  useEffect(() => () => { if (poll.current) clearTimeout(poll.current); }, []);

  const running = !!(status && (status.running === true || status.job?.running === true));
  // While a build runs, re-read the status every 3 s; on finish, refresh the rest.
  useEffect(() => {
    if (!running) return;
    poll.current = setTimeout(async () => {
      const d = await loadStatus();
      if (d && !(d.running === true || d.job?.running === true)) {
        void loadBuilds(); void loadLists(industry); void loadPool();
      }
    }, 3000);
    return () => { if (poll.current) clearTimeout(poll.current); };
  }, [running, status, loadStatus, loadBuilds, loadLists, loadPool, industry]);

  const rebuild = async (only?: string) => {
    setBusy('rebuild'); setMsg(null);
    const { error } = await panelPost(S, 'picks_rebuild', only ? { industries: [only] } : {});
    setBusy('');
    if (error) { setMsg({ kind: 'error', text: error }); return; }
    setMsg({ kind: 'ok', text: only ? `Rebuilding the ${only} lists…` : 'Rebuilding every list…' });
    void loadStatus();
  };
  const savePoolSize = async () => {
    const n = Number(poolSize);
    if (!Number.isInteger(n) || n < 20 || n > 30) { setMsg({ kind: 'error', text: 'The pool holds 20 to 30 technologies.' }); return; }
    setBusy('pool'); setMsg(null);
    const { error } = await panelPost(S, 'pool_set', {
      pool_size: n, pinned: pool?.pinned_ids || [], excluded: pool?.excluded_ids || [],
    });
    setBusy('');
    if (error) { setMsg({ kind: 'error', text: error }); return; }
    setMsg({ kind: 'ok', text: `Pool size saved (${n}). It applies from the next build.` });
    void loadPool();
  };

  const last = status ? first(status.last_build, status.last, status.build) : null;
  const listsCount = status ? first(status.lists, status.list_counts) : null;
  const thin = status ? asArray(first(status.thin, status.thin_lists)) : [];
  const nextCheck = status ? first(status.next_check_at, status.next_check) : null;
  const corpusFp = status?.corpus && typeof status.corpus === 'object' ? status.corpus : null;
  const chipsFp = status?.chips && typeof status.chips === 'object' ? status.chips : null;

  return (
    <div className="flex flex-col gap-4">
      <Section title="Picks status"
        right={
          <span className="flex items-center gap-2">
            <AdminLink to={{ group: 'system' }}
              className="text-xs text-primary-700 underline underline-offset-2 whitespace-nowrap">System → Search indexes</AdminLink>
            <Btn small kind="primary" onClick={() => rebuild()} disabled={running || busy === 'rebuild'}
              title="Rebuild every list from the corpus now (about a minute; runs in the background)">
              {running ? 'Building…' : busy === 'rebuild' ? 'Starting…' : 'Refresh now'}
            </Btn>
          </span>
        }>
        {statusErr && <Msg kind="error">Could not read the picks status: {statusErr}</Msg>}
        {!status && !statusErr && <Loading />}
        {status && (
          <>
            <KPIRow>
              <KPI label="Last build" value={last ? ago(first(last.finished_at, last.started_at)) : 'never'}
                hint={last ? `${asStr(first(last.trigger, '')) || 'build'}${num(last.seconds) !== null ? ` · ${Math.round(num(last.seconds)! * 10) / 10} s` : ''}${last.built_by ? ` · ${last.built_by}` : ''}` : undefined} />
              <KPI label="Lists" value={
                listsCount && typeof listsCount === 'object'
                  ? `${fmt(first(listsCount.keyword, 0))} + ${fmt(first(listsCount.industry, 0))} + ${fmt(first(listsCount.pool, 0))}`
                  : fmt(listsCount)} hint="keyword + industry + pool" />
              <KPI label="Thin lists" value={fmt(thin.length || first(status.thin_count, 0))} hint="under 3 candidates" />
              <KPI label="Ids gone since the build" value={fmt(first(status.ids_missing, status.missing, 0))}
                hint="replaced by back-ups when served" />
              <KPI label="Next check" value={nextCheck ? fmtDate(nextCheck) : '—'}
                hint={status.auto === false ? 'background checks off (ONBOARDING_PICKS_AUTO=0)'
                  : status.stale ? `due: ${asArray(status.stale_reasons).join(', ') || 'yes'}` : 'up to date'} />
            </KPIRow>
            {last?.error && <Msg kind="error">Last build failed: {String(last.error)}</Msg>}
            <div className="text-[11px] text-grayscale-1100 flex flex-wrap gap-x-4">
              {corpusFp ? (
                <span>
                  Corpus {fmt(corpusFp.count)} now · {fmt(corpusFp.built_count)} at the last full build
                  {corpusFp.changed ? ' — changed since' : ''}
                </span>
              ) : last?.corpus_count !== undefined && <span>Corpus at build: {fmt(last.corpus_count)}</span>}
              {chipsFp ? (
                <span>Chips {asStr(chipsFp.fp) || '—'}{chipsFp.built_fp ? ` (built ${chipsFp.built_fp})` : ''}{chipsFp.changed ? ' — changed since' : ''}</span>
              ) : last?.chips_fp && <span>chips {String(last.chips_fp)}</span>}
              {num(status.ids_missing) ? (
                <span title={asArray(status.ids_missing_sample).join(', ')}>
                  Gone: {asArray(status.ids_missing_sample).slice(0, 5).join(', ')}{num(status.ids_missing)! > 5 ? ' …' : ''}
                </span>
              ) : null}
            </div>
            {thin.length > 0 && (
              <details>
                <summary className="text-xs text-amber-700 cursor-pointer select-none">
                  {thin.length} thin list{thin.length === 1 ? '' : 's'} — topped up from the industry list, then the pool
                </summary>
                <div className="flex flex-wrap gap-1.5 mt-2">
                  {thin.map((t: any, i: number) => {
                    const ind = asStr(typeof t === 'string' ? t.split('|')[0] : first(t.industry, ''));
                    const kw = asStr(typeof t === 'string' ? (t.split('|')[1] || '') : first(t.keyword, ''));
                    const n = typeof t === 'string' ? null : num(first(t.candidates, t.n));
                    return (
                      <button key={i} type="button" onClick={() => ind && setIndustry(ind)}
                        className="text-xs px-2.5 py-1 rounded-full border border-amber-200 bg-amber-50 text-amber-800 hover:bg-amber-100">
                        {ind}{kw ? ` · ${kw}` : ' (industry)'}{n !== null ? ` — ${n}` : ''}
                      </button>
                    );
                  })}
                </div>
              </details>
            )}
          </>
        )}
        {msg && <Msg kind={msg.kind}>{msg.text}</Msg>}
      </Section>

      <Section title="Lists by industry"
        right={
          <span className="flex items-center gap-2">
            <select value={industry} onChange={e => setIndustry(e.target.value)}
              className="h-9 px-2 rounded-md border-[0.5px] border-primary-300 bg-white text-sm max-w-[18rem]">
              {names.map(n => <option key={n} value={n}>{n}</option>)}
            </select>
            <Btn small onClick={() => rebuild(industry)} disabled={running || !industry}
              title="Rebuild only this industry's 7 lists">Rebuild this industry</Btn>
          </span>
        }>
        {listsErr && <Msg kind="error">{listsErr}</Msg>}
        {lists === null ? <Loading /> : lists.length === 0 && !listsErr
          ? <div className="text-sm text-grayscale-1100">No stored lists for {industry} yet — press Refresh now.</div>
          : (
            <div className="grid xl:grid-cols-2 gap-3">
              {lists.map(l => <ListCard key={`${l.industry}|${l.keyword}`} list={l} onChanged={() => loadLists(industry)} />)}
            </div>
          )}
      </Section>

      <Section title="Random pool"
        right={
          <span className="flex items-center gap-2 text-xs text-grayscale-1100">
            Size
            <input type="number" min={20} max={30} value={poolSize} onChange={e => setPoolSize(e.target.value)}
              className="h-8 w-16 px-2 rounded-md border-[0.5px] border-primary-300 bg-white text-sm" />
            <Btn small onClick={savePoolSize} disabled={busy === 'pool'}>{busy === 'pool' ? 'Saving…' : 'Save size'}</Btn>
          </span>
        }>
        <p className="text-[13px] text-grayscale-1200">
          Explorers who skip onboarding (or pick “Others”) get 3 of these, chosen per person and kept the same on every
          visit, from 3 different industries and universities where possible.
        </p>
        {pool ? <ListCard list={pool} onChanged={loadPool} />
          : <div className="text-sm text-grayscale-1100">No pool stored yet — press Refresh now.</div>}
      </Section>

      <Section title="Build history">
        <DataTable rows={builds.map(b => ({
          started: b.started_at, finished: b.finished_at, trigger: b.trigger, by: b.built_by,
          lists: b.lists, thin: Array.isArray(b.thin) ? b.thin.length : b.thin, corpus: b.corpus_count,
          seconds: num(b.seconds) !== null ? Math.round(num(b.seconds)! * 10) / 10 : null, error: b.error,
        }))} empty="No builds yet." csvName="onboarding_picks_builds" maxHeight="20rem" />
      </Section>
    </div>
  );
};

export default Picks;
